"use client";

import { useId, type InputHTMLAttributes } from "react";
import { Check } from "lucide-react";

type CheckboxCardProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  name: string;
  label: string;
  description?: string;
};

export default function CheckboxCard({ name, label, description, id: providedId, value = "true", "aria-describedby": describedBy, ...inputProps }: CheckboxCardProps) {
  const generatedId = useId();
  const id = providedId ?? generatedId;

  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#29292c] bg-[#111114] p-3.5 transition-colors has-checked:border-[#fb454e] has-checked:bg-[#1b1114] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-focus has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-grey-900 sm:p-4">
      <input
        type="checkbox"
        id={id}
        name={name}
        value={value}
        aria-labelledby={`${id}-label`}
        aria-describedby={[description ? `${id}-description` : "", describedBy].filter(Boolean).join(" ") || undefined}
        className="peer sr-only"
        {...inputProps}
      />
      <span aria-hidden="true" className="mt-0.5 flex size-[22px] shrink-0 items-center justify-center rounded-md border-2 border-[#68686b] peer-checked:border-[#fb454e] peer-checked:bg-[#fb454e] peer-checked:[&_svg]:opacity-100">
        <Check className="size-4 text-white opacity-0" strokeWidth={3} />
      </span>
      <span className="min-w-0">
        <span id={`${id}-label`} className="block text-[15px] font-medium leading-6 text-white">{label}</span>
        {description && <span id={`${id}-description`} className="mt-1 block text-[13px] leading-relaxed text-grey-550">{description}</span>}
      </span>
    </label>
  );
}
